import React, { useState } from "react";
import { Formik, Field, Form } from "formik";
import {
    FieldWrappers,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeading,
    ModalSubHeading
} from "../Styles/InputStyles";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import CategoryCard from "../CategoryCard";
import SelectOptionModal from "../SelectOptionModal";

const BasicDetails = ({ onClose, setSelectedForm, setFormData }) => {
    const [isAssetTypeModalOpen, setIsAssetTypeModalOpen] = useState(false);
    const [isAssetSubTypeModalOpen, setIsAssetSubTypeModalOpen] = useState(false);

    const initialValues = {
        quantity: "",
        assetType: "",
        assetSubType: "",
        description: "",
    };

    const validationSchema = Yup.object({
        assetType: Yup.string().required("Asset type is required"),
        quantity: Yup.number()
            .required("Quantity is required")
            .min(1, "Units should not be less than 1")
            .max(10000, "Units should be less than 10000"),
        assetSubType: Yup.string().required("Asset sub-type is required"),
    });

    const handleSubmit = (values) => {
        setFormData((prevFormData) => ({
            ...prevFormData,
            basicDetails: values,
        }));
        setSelectedForm("2");
    };

    const assetTypeMapping = {
        "Furniture & Fixtures": 1,
        "Electrical & Appliances": 2,
        "Plumbing & Sanitary": 3,
        "Security & Access Control": 4,
        "HVAC (Heating, Ventilation & Air Conditioning)": 5,
        "Outdoor & Common Area Assets": 6,
        "IT & Networking Equipment": 7,
        "Kitchen Equipment & Accessories": 8,
        "Miscellaneous Assets": 9
    };

    const assetOptions = {
        "Furniture & Fixtures": [
            "Sofa",
            "Dining Table",
            "Chairs",
            "Beds",
            "Mattress",
            "Wardrobe",
            "Coffee Table",
            "Study Table",
            "Bookshelf",
            "Shoe Rack",
            "Curtains & Blinds"
        ],
        "Electrical & Appliances": [
            "Air Conditioner (AC)",
            "Refrigerator",
            "Microwave Oven",
            "Washing Machine",
            "Ceiling Fan",
            "Geyser/Water Heater",
            "Water Purifier",
            "Toaster",
            "Elevator",
            "Modem / Routers",
            "Solar water heater",
            "Heat pump",
            "Induction Stove/Electric Stove",
            "LED/LCD TV",
            "Home Inverter & Battery",
            "CCTV Camera"
        ],
        "Plumbing & Sanitary": [
            "Water Pump",
            "Overhead Water Tank",
            "Sink & Faucet",
            "Toilet & Flush System",
            "Water Heater/Geyser",
            "Shower Set",
            "Drainage Pipes"
        ],
        "Security & Access Control": [
            "Door Locks & Smart Locks",
            "CCTV System",
            "Biometric Access System",
            "Intercom System",
            "Fire Alarm & Smoke Detectors",
            "Security Lights"
        ],
        "HVAC (Heating, Ventilation & Air Conditioning)": [
            "Centralized AC Unit",
            "Exhaust Fans",
            "Ventilation Ducts",
            "Air Purifier"
        ],
        "Outdoor & Common Area Assets": [
            "Garden Furniture",
            "Streetlights",
            "Parking Barriers",
            "Solar Panels",
            "Generator/Power Backup"
        ],
        "IT & Networking Equipment": [
            "WiFi Router",
            "Network Switch",
            "Server System",
            "Landline Phone"
        ],
        "Kitchen Equipment & Accessories": [
            "Gas Stove & Cylinder",
            "Chimney & Exhaust Fan",
            "Water Filter",
            "Dish Washer",
            "Kitchen Cabinets"
        ],
        "Miscellaneous Assets": [
            "Fire Extinguishers",
            "Elevators & Escalators",
            "Storage Racks",
            "Bulletin Boards & Notice Boards",
            "Signage & Nameplates",
            "Vehicle"
        ]
    };

    const assetTypeOptions = Object.keys(assetTypeMapping).map(type => ({
        label: type,
        value: assetTypeMapping[type]
    }));

    const getAssetSubTypeOptions = (selectedAssetType) => {
        const assetTypeName = Object.keys(assetTypeMapping).find(
            key => assetTypeMapping[key] === selectedAssetType
        );

        if (assetTypeName && assetOptions[assetTypeName]) {
            return assetOptions[assetTypeName].map(subType => ({
                label: subType,
                value: subType
            }));
        }
        return [];
    };

    const getAssetTypeLabel = (assetTypeValue) => {
        const assetType = assetTypeOptions.find(option => option.value === assetTypeValue);
        return assetType ? assetType.label : "Select Asset Type";
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({ values, setFieldValue, errors, touched }) => (
                <Form>
                    <ModalSubHeading>Step 1</ModalSubHeading>
                    <ModalHeading>Basic Details</ModalHeading>
                    <ScrollableContent>
                        <Wrapper>
                            <InputGroup>
                                <Label>Asset Type *</Label>
                                <CategoryCard
                                    title="Asset Type"
                                    selectedCategory={
                                        values.assetType
                                            ? {
                                                label: getAssetTypeLabel(values.assetType),
                                                value: values.assetType
                                            }
                                            : null
                                    }
                                    onClick={() => setIsAssetTypeModalOpen(true)}
                                />
                                {touched.assetType && errors.assetType && (
                                    <ErrorText>{errors.assetType}</ErrorText>
                                )}
                            </InputGroup>

                            {values.assetType && (
                                <InputGroup>
                                    <Label>Select Asset Sub-Type *</Label>
                                    <CategoryCard
                                        title="Asset Sub-Type"
                                        selectedCategory={
                                            values.assetSubType
                                                ? {
                                                    label: values.assetSubType,
                                                    value: values.assetSubType
                                                }
                                                : null
                                        }
                                        onClick={() => setIsAssetSubTypeModalOpen(true)}
                                    />
                                    {touched.assetSubType && errors.assetSubType && (
                                        <ErrorText>{errors.assetSubType}</ErrorText>
                                    )}
                                </InputGroup>
                            )}

                            <InputGroup>
                                <Label htmlFor="quantity">Total Units *</Label>
                                <FieldWrappers>
                                    <Field
                                        type="text"
                                        id="quantity"
                                        name="quantity"
                                        inputMode="numeric"
                                        value={values.quantity}
                                        autoComplete="off"
                                        onKeyPress={(e) => {
                                            if (!/[0-9]/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={10}
                                    />
                                </FieldWrappers>
                                {touched.quantity && errors.quantity && (
                                    <ErrorText>{errors.quantity}</ErrorText>
                                )}
                            </InputGroup>

                            <InputGroup>
                                <Label htmlFor="description">Description </Label>
                                <Field
                                    as={Textarea}
                                    id="description"
                                    name="description"
                                    rows={5}
                                    placeholder="Enter Description (e.g., Serial Number or Product Specifications)"
                                />
                            </InputGroup>
                        </Wrapper>
                    </ScrollableContent>
                    <ActionSection>
                        <SideCancelBtn type="button" onClick={onClose}>
                            Cancel
                        </SideCancelBtn>
                        <SubmitBtn type="submit">
                            Next
                        </SubmitBtn>
                    </ActionSection>

                    <SelectOptionModal
                        isOpen={isAssetTypeModalOpen}
                        onClose={() => setIsAssetTypeModalOpen(false)}
                        title="Select Asset Type"
                        options={assetTypeOptions}
                        selectedValue={values.assetType}
                        onSelect={(selectedValue) => {
                            setFieldValue("assetType", selectedValue);
                            setFieldValue("assetSubType", "");
                        }}
                        searchPlaceholder="Search asset type..."
                    />

                    {values.assetType && (
                        <SelectOptionModal
                            isOpen={isAssetSubTypeModalOpen}
                            onClose={() => setIsAssetSubTypeModalOpen(false)}
                            title="Select Asset Sub-Type"
                            options={getAssetSubTypeOptions(values.assetType)}
                            selectedValue={values.assetSubType}
                            onSelect={(selectedValue) => {
                                setFieldValue("assetSubType", selectedValue);
                            }}
                            searchPlaceholder="Search asset sub-type..."
                        />
                    )}
                </Form>
            )}
        </Formik>
    );
};

const Textarea = tw.textarea`min-h-10 max-h-40 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-20 resize-y break-words overflow-hidden`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const InputGroup = tw.div`w-full flex flex-col mt-2 md:mt-0`;
const Label = tw.label`text-sm text-gray-600 font-normal mb-1.5`;

export default BasicDetails;